<template>
  <div class="dashboard-shell">
    <AppTopbar
      :user-name="userFullName"
      :logout-text="get_word('logout') || 'התנתקות'"
    />

    <main class="page-shell">
      <div class="container-readable">
        <!-- כותרת העמוד -->
        <section class="support-hero">
          <div class="support-hero-content">
            <div class="support-hero-icon">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
                <line x1="8" y1="9" x2="16" y2="9" />
                <line x1="8" y1="13" x2="14" y2="13" />
              </svg>
            </div>

            <div>
              <span class="support-eyebrow">שירות לקוחות</span>

              <h1>פתיחת פנייה</h1>

              <p>
                מלאו את פרטי הפנייה ונציג השירות יחזור אליכם.
                התשובה תישלח לכתובת המייל שתזינו בטופס.
              </p>
            </div>
          </div>

          <router-link to="/" class="back-link">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>

            <span>חזרה לאזור האישי</span>
          </router-link>
        </section>

        <div class="support-layout">
          <!-- טופס הפנייה -->
          <section class="support-card">
            <div class="card-heading">
              <div>
                <h2>פרטי הפנייה</h2>
                <p>השדות המסומנים בכוכבית הם שדות חובה.</p>
              </div>

              <span class="secure-badge">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <rect x="3" y="11" width="18" height="10" rx="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>

                מאובטח
              </span>
            </div>

            <!-- הודעת הצלחה -->
            <div
              v-if="successMessage"
              class="form-message success-message"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M20 6L9 17l-5-5" />
              </svg>

              <span>{{ successMessage }}</span>
            </div>

            <!-- הודעת שגיאה -->
            <div
              v-if="errorMessage"
              class="form-message error-message"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>

              <span>{{ errorMessage }}</span>
            </div>

            <form @submit.prevent="submitTicket">
              <div class="form-grid">
                <!-- נושא הפנייה -->
                <div class="form-group">
                  <label for="category">
                    נושא הפנייה
                    <span class="required">*</span>
                  </label>

                  <div class="input-wrapper">
                    <svg
                      class="field-icon"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                    </svg>

                    <select
                      id="category"
                      v-model="form.category"
                      :disabled="sending"
                      required
                    >
                      <option value="">בחרו נושא</option>
                      <option value="billing">חשבוניות ותשלומים</option>
                      <option value="network">קליטה ורשת</option>
                      <option value="plan">מסלול וחבילות</option>
                      <option value="technical">תקלה טכנית</option>
                      <option value="other">נושא אחר</option>
                    </select>
                  </div>
                </div>

                <!-- מספר מנוי -->
                <div class="form-group">
                  <label for="phone">מספר מנוי</label>

                  <div class="input-wrapper">
                    <svg
                      class="field-icon"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <rect x="5" y="2" width="14" height="20" rx="2" />
                      <line x1="12" y1="18" x2="12.01" y2="18" />
                    </svg>

                    <select
                      id="phone"
                      v-model="form.phone"
                      :disabled="sending"
                    >
                      <option value="">פנייה כללית</option>

                      <option
                        v-for="number in numbers"
                        :key="number.id || number.phone"
                        :value="number.phone"
                      >
                        {{ formatPhone(number.phone) }}
                        {{ number.plan_name ? `- ${number.plan_name}` : '' }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>

              <!-- כותרת -->
              <div class="form-group">
                <label for="subject">
                  כותרת הפנייה
                  <span class="required">*</span>
                </label>

                <div class="input-wrapper">
                  <svg
                    class="field-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <line x1="4" y1="9" x2="20" y2="9" />
                    <line x1="4" y1="15" x2="20" y2="15" />
                    <line x1="10" y1="3" x2="8" y2="21" />
                    <line x1="16" y1="3" x2="14" y2="21" />
                  </svg>

                  <input
                    id="subject"
                    v-model.trim="form.subject"
                    type="text"
                    maxlength="150"
                    placeholder="לדוגמה: בירור בנוגע לחיוב האחרון"
                    :disabled="sending"
                    required
                  />
                </div>
              </div>

              <!-- תוכן הפנייה -->
              <div class="form-group">
                <div class="label-row">
                  <label for="message">
                    תיאור הפנייה
                    <span class="required">*</span>
                  </label>

                  <span class="character-counter">
                    {{ form.message.length }}/3000
                  </span>
                </div>

                <textarea
                  id="message"
                  v-model.trim="form.message"
                  rows="7"
                  maxlength="3000"
                  placeholder="כתבו כאן את פרטי הפנייה בצורה ברורה ככל האפשר..."
                  :disabled="sending"
                  required
                ></textarea>

                <span class="field-help">
                  מומלץ לציין תאריכים, מספרי חיוב או פרטים נוספים
                  שיסייעו לטיפול מהיר.
                </span>
              </div>

              <!-- מייל -->
              <div class="form-group">
                <label for="email">
                  כתובת מייל לקבלת תשובה
                  <span class="required">*</span>
                </label>

                <div class="input-wrapper">
                  <svg
                    class="field-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22 6 12 13 2 6" />
                  </svg>

                  <input
                    id="email"
                    v-model.trim="form.email"
                    type="email"
                    placeholder="name@example.com"
                    :disabled="sending"
                    required
                  />
                </div>

                <span class="field-help">
                  תשובת נציג השירות תישלח לכתובת זו.
                </span>
              </div>

              <div class="form-footer">
                <p class="privacy-text">
                  בלחיצה על שליחת הפנייה, הפרטים יועברו לצוות
                  שירות הלקוחות.
                </p>

                <button
                  type="submit"
                  class="submit-button"
                  :disabled="sending"
                >
                  <span
                    v-if="sending"
                    class="button-loader"
                  ></span>

                  <svg
                    v-else
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <line x1="22" y1="2" x2="11" y2="13" />
                    <polygon points="22 2 15 22 11 13 2 9 22 2" />
                  </svg>

                  <span>
                    {{ sending ? 'הפנייה נשלחת...' : 'שליחת הפנייה' }}
                  </span>
                </button>
              </div>
            </form>
          </section>

          <!-- מידע צדדי -->
          <aside class="support-sidebar">
            <section class="info-card">
              <div class="info-icon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="16" x2="12" y2="12" />
                  <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
              </div>

              <h3>לפני ששולחים</h3>

              <ul>
                <li>בחרו את נושא הפנייה המתאים ביותר.</li>
                <li>ציינו את מספר המנוי הרלוונטי.</li>
                <li>כתבו תיאור מפורט וברור.</li>
                <li>ודאו שכתובת המייל נכונה.</li>
              </ul>
            </section>

            <section class="response-card">
              <div class="response-icon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
              </div>

              <div>
                <span>לאחר שליחת הפנייה</span>
                <strong>נציג השירות יטפל בבקשה</strong>

                <p>
                  התשובה תישלח ישירות לכתובת המייל שהזנתם.
                </p>
              </div>
            </section>
          </aside>
        </div>
      </div>
    </main>

    <AppFooter />
  </div>
</template>

<script>
import AppTopbar from '@/components/AppTopbar.vue'
import AppFooter from '@/components/AppFooter.vue'

export default {
  name: 'SupportTicket',

  components: {
    AppTopbar,
    AppFooter
  },

  data() {
    return {
      sending: false,
      successMessage: '',
      errorMessage: '',

      user: null,
      numbers: [],

      form: {
        category: '',
        phone: '',
        subject: '',
        message: '',
        email: ''
      }
    }
  },

  computed: {
    userFullName() {
      if (!this.user) {
        return ''
      }

      return `${this.user.first_name || ''} ${
        this.user.last_name || ''
      }`.trim()
    }
  },

  mounted() {
    this.loadUserData()
  },

  methods: {
    loadUserData() {
      this.api(
        {
          action: 'get_my_dashboard_data',
          method: 'get'
        },
        (response) => {
          if (!response?.data?.success) {
            this.errorMessage = 'לא הצלחנו לטעון את פרטי המשתמש.'
            return
          }

          this.user = response.data.user || null

          this.numbers = Array.isArray(response.data.numbers)
            ? response.data.numbers
            : []

          // טעינת כתובת המייל של המשתמש לתוך הטופס
          if (this.user?.email) {
            this.form.email = this.user.email
          }

          // אם קיים רק מספר אחד, בוחרים אותו אוטומטית
          if (this.numbers.length === 1) {
            this.form.phone = this.numbers[0].phone || ''
          }
        },
        () => {
          this.errorMessage = 'אירעה שגיאה בטעינת פרטי המשתמש.'
        }
      )
    },

    submitTicket() {
      this.successMessage = ''
      this.errorMessage = ''

      if (!this.validateForm()) {
        return
      }

      this.sending = true

      this.api(
        {
          action: 'send_support_ticket',
          method: 'post',
          data: {
            category: this.form.category,
            phone: this.form.phone,
            subject: this.form.subject,
            message: this.form.message,
            email: this.form.email
          }
        },
        (response) => {
          if (response?.data?.success) {
            this.successMessage =
              'הפנייה נשלחה בהצלחה. נציג יחזור אליך במייל בהקדם.'

            // מנקים את תוכן הפנייה אך משאירים את כתובת המייל
            this.form.category = ''
            this.form.subject = ''
            this.form.message = ''

            // אם יש יותר ממספר מנוי אחד, מנקים את הבחירה
            if (this.numbers.length !== 1) {
              this.form.phone = ''
            }

            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            })
          } else {
            const error = response?.data?.error

            if (error === 'invalid_email') {
              this.errorMessage = 'כתובת המייל אינה תקינה.'
            } else if (error === 'missing_fields') {
              this.errorMessage = 'יש למלא את כל שדות החובה.'
            } else if (error === 'mail_failed') {
              this.errorMessage =
                'הפנייה התקבלה, אך אירעה שגיאה בשליחת המייל.'
            } else if (error === 'not_logged_in') {
              this.errorMessage =
                'החיבור למערכת הסתיים. יש להתחבר מחדש.'
            } else {
              this.errorMessage =
                'לא הצלחנו לשלוח את הפנייה. נסו שוב.'
            }
          }

          this.sending = false
        },
        () => {
          this.errorMessage =
            'לא ניתן להתחבר לשרת. נסו שוב מאוחר יותר.'

          this.sending = false
        }
      )
    },

    validateForm() {
      if (!this.form.category) {
        this.errorMessage = 'יש לבחור נושא לפנייה.'
        return false
      }

      if (!this.form.subject || this.form.subject.length < 3) {
        this.errorMessage =
          'יש להזין כותרת באורך של לפחות 3 תווים.'
        return false
      }

      if (!this.form.message || this.form.message.length < 10) {
        this.errorMessage =
          'יש להזין תיאור מפורט יותר לפנייה.'
        return false
      }

      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

      if (!emailPattern.test(this.form.email)) {
        this.errorMessage = 'כתובת המייל אינה תקינה.'
        return false
      }

      return true
    },

    formatPhone(phone) {
      if (!phone) {
        return ''
      }

      const cleanPhone = String(phone).replace(/\D/g, '')

      if (
        cleanPhone.startsWith('972') &&
        cleanPhone.length === 12
      ) {
        return `0${cleanPhone.slice(3)}`
      }

      return String(phone)
    }
  }
}
</script>



<style lang="scss" scoped src="@\styles\pages\SupportTicket.scss"></style>

